import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Calendar, CheckCircle, XCircle, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { stripeService } from '../services/api';

// Initialisation de Stripe avec la clé publique depuis les variables d'environnement
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...');

const SubscriptionManagement = ({ refreshTrigger, onRefreshComplete }) => {
  const { state, actions } = useApp();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  // Charger les abonnements au montage
  useEffect(() => {
    loadSubscriptions();
  }, []);

  // Gérer le rafraîchissement automatique
  useEffect(() => {
    if (refreshTrigger) {
      loadSubscriptions().then(() => {
        if (onRefreshComplete) {
          onRefreshComplete();
        }
      });
    }
  }, [refreshTrigger, onRefreshComplete]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Utiliser le service API pour récupérer les abonnements
      const response = await stripeService.getUserSubscriptions();
      setSubscriptions(response.data || []);
      
    } catch (err) {
      console.error('Erreur chargement abonnements:', err);
      setError(err.message);
      actions.addNotification({
        type: 'error',
        message: `Erreur lors du chargement des abonnements: ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      
      // Créer une session de checkout Stripe
      const checkoutData = await stripeService.createCheckoutSession('time_tracker_pro_monthly');
      
      if (checkoutData.url) {
        // Rediriger vers Stripe Checkout
        window.location.href = checkoutData.url;
      } else {
        throw new Error('URL de checkout non reçue');
      }
    } catch (err) {
      console.error('Erreur lors de la souscription:', err);
      actions.addNotification({
        type: 'error',
        message: `Erreur lors de la souscription: ${err.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);
      
      // Créer une session de portail client Stripe
      // Pour cela, nous devons d'abord vérifier si l'utilisateur a un customer_id Stripe
      if (subscriptions.length === 0) {
        actions.addNotification({
          type: 'warning',
          message: 'Vous devez avoir au moins un abonnement pour accéder au portail de gestion.'
        });
        return;
      }

      // Utiliser le service API pour créer la session portail
      const portalData = await stripeService.createPortalSession();
      
      if (portalData.url) {
        // Rediriger vers le portail client Stripe
        window.open(portalData.url, '_blank');
        actions.addNotification({
          type: 'success',
          message: 'Le portail de gestion Stripe s\'ouvre dans un nouvel onglet.'
        });
      } else {
        throw new Error('URL du portail non reçue');
      }
    } catch (err) {
      console.error('Erreur portail Stripe:', err);
      actions.addNotification({
        type: 'error',
        message: `Erreur lors de l'ouverture du portail de gestion: ${err.message}`
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Actif':
        return 'text-green-600 bg-green-100';
      case 'Période d\'essai':
        return 'text-blue-600 bg-blue-100';
      case 'Annulé':
        return 'text-red-600 bg-red-100';
      case 'Paiement en retard':
        return 'text-orange-600 bg-orange-100';
      case 'Suspendu':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Actif':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'Période d\'essai':
        return <AlertCircle size={16} className="text-blue-600" />;
      case 'Annulé':
        return <XCircle size={16} className="text-red-600" />;
      case 'Paiement en retard':
        return <AlertCircle size={16} className="text-orange-600" />;
      default:
        return <AlertCircle size={16} className="text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center space-x-2">
          <RefreshCw className="animate-spin h-5 w-5 text-blue-600" />
          <span className="text-gray-600">Chargement des abonnements...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <XCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800 font-medium">Erreur</span>
        </div>
        <p className="text-red-700 mt-1">{error}</p>
        <button
          onClick={loadSubscriptions}
          className="mt-3 inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm text-red-700 bg-white hover:bg-red-50"
        >
          <RefreshCw size={14} className="mr-1" />
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec action principale */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gestion des abonnements</h3>
          <p className="mt-1 text-sm text-gray-500">
            Gérez vos abonnements, méthodes de paiement et factures.
          </p>
        </div>
        <button
          onClick={handleManageSubscription}
          disabled={portalLoading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {portalLoading ? (
            <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
          ) : (
            <ExternalLink className="-ml-1 mr-2 h-4 w-4" />
          )}
          Portail de gestion
        </button>
      </div>

      {/* Plan d'abonnement si pas d'abonnement actif */}
      {subscriptions.length === 0 ? (
        <SubscriptionPlan onSubscribe={handleSubscribe} />
      ) : (
        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <div key={subscription.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-lg font-medium text-gray-900">
                      {subscription.produit}
                    </h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(subscription.statut)}`}>
                      {getStatusIcon(subscription.statut)}
                      <span className="ml-1">{subscription.statut}</span>
                    </span>
                  </div>
                  
                  <p className="mt-1 text-sm text-gray-600">
                    Plan: <span className="font-medium">{subscription.plan}</span>
                  </p>
                  
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Prix:</span>
                      <p className="font-medium text-gray-900">{subscription.montant}</p>
                      <p className="text-gray-500">{subscription.facturation}</p>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Date de début:</span>
                      <p className="font-medium text-gray-900 flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(subscription.dateDebut)}
                      </p>
                    </div>
                    
                    <div>
                      <span className="text-gray-500">Prochaine période:</span>
                      <p className="font-medium text-gray-900 flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {formatDate(subscription.prochainePeriode)}
                      </p>
                      {subscription.expireBientot && (
                        <p className="text-orange-600 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          Expire bientôt
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {subscription.estEnEssai && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <span className="ml-2 text-sm text-blue-800">
                      Vous êtes actuellement en période d'essai gratuite.
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions supplémentaires */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Actions rapides</h4>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={loadSubscriptions}
            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw size={14} className="mr-1" />
            Actualiser
          </button>
          
          <button
            onClick={() => {/* TODO: Implémenter la vue des factures */}}
            className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <CreditCard size={14} className="mr-1" />
            Mes factures
          </button>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher le plan d'abonnement
const SubscriptionPlan = ({ onSubscribe }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      await onSubscribe();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-6 border border-blue-200">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
          <CreditCard className="w-8 h-8 text-white" />
        </div>
        
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Abonnement Time Tracker</h3>
        <p className="text-gray-600 mb-6">
          Accédez à toutes les fonctionnalités de Time Tracker pour optimiser votre gestion du temps.
        </p>

        {/* Prix */}
        <div className="mb-6">
          <div className="flex items-center justify-center">
            <span className="text-4xl font-bold text-blue-600">5€</span>
            <span className="text-gray-500 ml-2">/mois</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Facturation mensuelle</p>
        </div>

        {/* Fonctionnalités */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Fonctionnalités incluses :</h4>
          <div className="space-y-3 text-left max-w-md mx-auto">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className="text-gray-700">Suivi du temps illimité</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className="text-gray-700">Gestion des projets et équipes</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className="text-gray-700">Rapports et statistiques détaillés</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className="text-gray-700">Facturation automatisée</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
              <span className="text-gray-700">Support prioritaire</span>
            </div>
          </div>
        </div>

        {/* Bouton de souscription */}
        <button
          onClick={handleSubscribe}
          disabled={isLoading}
          className="inline-flex items-center px-8 py-3 border border-transparent text-lg font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          {isLoading ? (
            <>
              <RefreshCw className="animate-spin -ml-1 mr-3 h-5 w-5" />
              Redirection en cours...
            </>
          ) : (
            <>
              <CreditCard className="-ml-1 mr-3 h-5 w-5" />
              S'abonner maintenant
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 mt-4">
          Paiement sécurisé par Stripe • Annulation possible à tout moment
        </p>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
